import React, { createContext, useContext, useState, useEffect } from "react";
import api, { TOKEN_KEY, USER_KEY } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // sessionStorage → clears automatically when tab/window/browser is closed.
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch((err) => {
        // 401 handled globally by axios interceptor; just log otherwise
        if (err?.response?.status !== 401) console.error("auth/me failed:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  // NOTE: previously we sent a `pagehide`/`beforeunload` sendBeacon to
  // /api/auth/logout so the login_session flipped to offline immediately.
  // That fired on ANY navigation (including F5 refresh and cross-tab
  // switches), which forced candidates to re-login after every reload —
  // a real UX regression. Session offline is now handled by:
  //   1. Explicit Logout button → POST /api/auth/logout
  //   2. Login on another device → server rotates current_session_id and
  //      marks the previous login_session offline (single-device policy)
  //   3. Tab close → sessionStorage clears, so the client is effectively
  //      logged out; DB row remains "online" until (1) or (2) fires.
  // This is an intentional trade-off: slightly less real-time
  // "Live Employees" accuracy, in exchange for reliable candidate sessions.

  const login = async (email, password) => {
    // Best-effort client device snapshot for forensic evidence — never used for auth logic.
    let device = null;
    try {
      const markers = [];
      const w = window;
      if (w.navigator && w.navigator.webdriver) markers.push("navigator.webdriver");
      if (w.__playwright__ || w.__pw_manualMouseDown) markers.push("playwright");
      if (w.__nightmare) markers.push("nightmare");
      if (w.callPhantom || w._phantom) markers.push("phantom");
      if (w.Cypress) markers.push("cypress");
      if (w.__selenium_unwrapped || w.__webdriver_evaluate || w.__driver_evaluate) markers.push("selenium");
      if (w.chrome && w.chrome.runtime && Object.keys(w.chrome.runtime).length > 0) {
        // extensions loaded — not proof of automation but worth logging
      }
      device = {
        platform: (w.navigator && (w.navigator.userAgentData?.platform || w.navigator.platform)) || null,
        language: (w.navigator && w.navigator.language) || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: w.screen ? `${w.screen.width}x${w.screen.height}` : null,
        webdriver: !!(w.navigator && w.navigator.webdriver),
        automation_markers: markers,
        hw_concurrency: (w.navigator && w.navigator.hardwareConcurrency) || null,
      };
    } catch (e) { /* device snapshot best-effort */ }

    const { data } = await api.post("/auth/login", { email, password, device });
    sessionStorage.setItem(TOKEN_KEY, data.token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("Logout request failed:", err); }
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    // Clean up any leftover legacy keys from older localStorage-based auth
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (e) { /* noop */ }
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
